import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useUser } from '../../hooks/useUser'
import { AdAccountInformation } from './AdAccountInformation'
import { RoundingStrategySettings } from './RoundingStrategy'
import { DefaultSmartBidPlusSettings } from './DefaultSmartBidPlusSettings'
import { DefaultSmartBidSettings } from './DefaultSmartBidSettings'
import { MyBidAndCampaignSettings } from './MyBidAndCampaignSettings'
import { ImpactMultiplier } from './ImpactMultiplier'
import { Layout } from '../../components/Layout'
import { BreadcrumbProps } from '@blueprintjs/core'
import { EnhancedProfileSelect } from '../../components/profiles/DefaultProfileSelect'
import { type EntityId, idKey } from '../../types/constants'

export const ProfileSettingsNew = observer(() => {
    const { activeProfile, profiles } = useUser()
    const [selectedProfileId, setSelectedProfileId] = useState<EntityId | null>(
        activeProfile?.profile_id ?? null,
    )
    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Settings' }]
    const selectedProfile = useMemo(() => {
        return (
            profiles.find((profile) => idKey(profile.profile_id) === idKey(selectedProfileId)) ??
            activeProfile
        )
    }, [activeProfile, profiles, selectedProfileId])

    useEffect(() => {
        if (!selectedProfileId && activeProfile) {
            setSelectedProfileId(activeProfile.profile_id)
        }
    }, [activeProfile, selectedProfileId])

    if (!selectedProfile) {
        return null
    }

    return (
        <Layout
            breadcrumbs={breadcrumbs}
            primary={selectedProfile.nickname ?? selectedProfile.name}
        >
            <div className="max-w-3xl mx-auto">
                <div className="grid grid-cols-1 gap-4 pb-8 pt-3 sm:grid-cols-[minmax(0,1fr)_360px] sm:items-start">
                    <div className="min-w-0">
                        <h1 className="m-0 text-[24px] font-[700] leading-none tracking-[-0.03em] text-[#242529]">
                            Ad Account Settings
                        </h1>
                        <p className="mt-1 mb-0 text-[14px] font-[400] leading-5 text-[#777A80]">
                            Manage settings for {selectedProfile.nickname ?? selectedProfile.name}.
                        </p>
                    </div>
                    <div className="flex justify-end">
                        <EnhancedProfileSelect
                            condensed
                            className="w-full sm:w-auto"
                            buttonProps={{ alignText: 'left' }}
                            selectedProfileId={selectedProfile.profile_id}
                            filter={(profiles) => profiles.filter((profile) => profile.managed)}
                            onChange={(profile) => setSelectedProfileId(profile.profile_id)}
                        />
                    </div>
                </div>
                <AdAccountInformation selectedProfile={selectedProfile} />
                <ImpactMultiplier selectedProfile={selectedProfile} />
                <MyBidAndCampaignSettings selectedProfile={selectedProfile} />
                <DefaultSmartBidSettings selectedProfile={selectedProfile} />
                <DefaultSmartBidPlusSettings selectedProfile={selectedProfile} />
                <RoundingStrategySettings selectedProfile={selectedProfile} />
            </div>
        </Layout>
    )
})
